import { Button, Input, Popconfirm, Segmented, Select, Tooltip, Typography } from 'antd';
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import type { EdgeKind, FlowEdge } from '@/flow-ir/types';
import { normalizeEdge } from '@/flow-ir/mutations';
import type { UiDiagnostic } from '@/flow-ir/diagnostics';

const { Text } = Typography;

const KIND_LABEL: Record<EdgeKind, string> = {
  sequence: 'Sequence / gate',
  status: 'Status-conditional',
  data: 'Data dependency',
};

export interface EdgeInspectorProps {
  edge: FlowEdge;
  editable: boolean;
  /** Status tokens the source node can emit (decision outcomes / human decisions). */
  statusOptions?: string[];
  diagnostics?: UiDiagnostic[];
  onChange?: (next: FlowEdge) => void;
  onDelete?: () => void;
  onClose: () => void;
}

/**
 * Edge configuration (spec §4.5 — three kinds, no fourth). The kind picker enforces the discipline
 * structurally: switching to DATA strips any status/guard (a data edge can NEVER gate execution);
 * switching to STATUS requires a status token; SEQUENCE may carry an optional CEL guard. This mirrors
 * the server analyser so the UI never even offers an unrepresentable edge (violations the server
 * still catches surface as inline diagnostics).
 */
export function EdgeInspector({
  edge,
  editable,
  statusOptions = [],
  diagnostics = [],
  onChange,
  onDelete,
  onClose,
}: EdgeInspectorProps) {
  const emit = (next: FlowEdge) => onChange?.(normalizeEdge(next));

  const setKind = (kind: EdgeKind) => {
    if (kind === edge.kind) return;
    // normalizeEdge drops the now-invalid fields for the new kind.
    emit({ ...edge, kind, onStatus: undefined, guard: undefined, binding: undefined });
  };

  return (
    <aside
      aria-label={`Edge inspector: ${edge.from} to ${edge.to}`}
      style={{
        width: 360,
        flex: 'none',
        height: '100%',
        borderInlineStart: '1px solid var(--ol-border)',
        background: 'var(--ol-bg-elevated)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid var(--ol-border)',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'var(--ol-text-tertiary)',
            }}
          >
            Edge
          </div>
          <div className="ol-mono" style={{ fontSize: 12.5, marginTop: 3 }}>
            {edge.from} <span style={{ color: 'var(--ol-text-tertiary)' }}>→</span> {edge.to}
          </div>
        </div>
        {editable && onDelete && (
          <Popconfirm title="Delete this edge?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={onDelete}>
            <Button type="text" size="small" danger aria-label="Delete edge" icon={<DeleteOutlined />} />
          </Popconfirm>
        )}
        <Button type="text" size="small" aria-label="Close inspector" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      <div style={{ overflow: 'auto', padding: '14px' }}>
        <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
          Edge kind
        </label>
        <div style={{ marginTop: 6 }}>
          <Segmented
            block
            size="small"
            disabled={!editable}
            value={edge.kind}
            onChange={(v) => setKind(v as EdgeKind)}
            options={[
              { label: 'Sequence', value: 'sequence' },
              { label: 'Status', value: 'status' },
              { label: 'Data', value: 'data' },
            ]}
          />
        </div>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
          {edge.kind === 'data'
            ? 'Binds an output to an input. A data edge NEVER gates execution.'
            : KIND_LABEL[edge.kind] + ' — orders execution (control layer).'}
        </Text>

        {edge.kind === 'status' && (
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
              On status <span style={{ color: 'var(--ol-negative)' }}>*</span>
            </label>
            <Tooltip title="The source node's completion status this branch routes on (closed vocabulary — no synonym invention).">
              <Select
                size="small"
                mode="tags"
                maxCount={1}
                style={{ width: '100%', marginTop: 4 }}
                disabled={!editable}
                placeholder="e.g. FAILED"
                value={edge.onStatus ? [edge.onStatus] : []}
                options={statusOptions.map((s) => ({ value: s, label: s }))}
                onChange={(v) => emit({ ...edge, onStatus: (v as string[])[0] || '' })}
              />
            </Tooltip>
          </div>
        )}

        {edge.kind === 'sequence' && (
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
              Guard (CEL, optional)
            </label>
            <Input
              className="ol-mono"
              size="small"
              style={{ marginTop: 4 }}
              disabled={!editable}
              placeholder="e.g. input.amount > 100000"
              value={edge.guard ?? ''}
              onChange={(e) => emit({ ...edge, guard: e.target.value || undefined })}
            />
          </div>
        )}

        {edge.kind === 'data' && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
                Binding source <span style={{ color: 'var(--ol-negative)' }}>*</span>
              </label>
              <Input
                className="ol-mono"
                size="small"
                style={{ marginTop: 4 }}
                disabled={!editable}
                placeholder="e.g. pan.name"
                value={edge.binding?.source ?? ''}
                onChange={(e) =>
                  emit({ ...edge, binding: { source: e.target.value, target: edge.binding?.target ?? '' } })
                }
              />
            </div>
            <div>
              <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
                Binding target <span style={{ color: 'var(--ol-negative)' }}>*</span>
              </label>
              <Input
                className="ol-mono"
                size="small"
                style={{ marginTop: 4 }}
                disabled={!editable}
                placeholder="e.g. applicant.name"
                value={edge.binding?.target ?? ''}
                onChange={(e) =>
                  emit({ ...edge, binding: { source: edge.binding?.source ?? '', target: e.target.value } })
                }
              />
            </div>
          </div>
        )}

        {diagnostics.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
              Issues
            </label>
            {diagnostics.map((d, i) => (
              <div key={i} className="ol-problem">
                <span
                  style={{
                    color: `var(--ol-${d.severity === 'error' ? 'negative' : d.severity === 'warning' ? 'attention' : 'info'})`,
                  }}
                >
                  {d.severity === 'error' ? '●' : d.severity === 'warning' ? '▲' : 'ℹ'}
                </span>
                <span>
                  <span className="ol-problem__code">{d.code}</span> {d.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
